import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CopyButton } from "../components/CopyButton";
import {
  type AdminInterviewDetail as InterviewDetailRow,
  type AdminStudy,
  type AdminUserDetails,
  ApiError,
  interviews as interviewsApi,
  studies as studiesApi,
} from "../lib/api";
import { ErrorBanner, formatApiError } from "./Studies";

/**
 * /interviews/:id — Interview detail (port of `_prototype` expanded-row +
 * `TranscriptModal` polish).
 *
 * Layout (top → bottom):
 *   - Back link to /interviews
 *   - Header: email + Sentiment pill + Status pill + archive button
 *   - 6 KPI tiles (Duration / Turns / Participant words / Participant share
 *     / Cost / Started)
 *   - Identity snapshot card (cms.users projection — name, language, plans)
 *   - Audio + Open-transcript row (audio player flexes; transcript opens
 *     in a full-screen modal per the prototype)
 *   - Insights card with Regenerate button + Sparkle icon header
 *   - Invalid-reason callout (when applicable)
 *
 * Heavy bits intentionally not ported:
 *   - UserInfoPopover with placeholder plan/platform data (we get the real
 *     thing from /user-details and render it as a card instead)
 *   - In-place transcript expand (we use the modal exclusively — cleaner
 *     for the standalone route)
 */
export function InterviewDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Row links carry the filtered list URL in history state, so Back returns to
  // the exact filtered view. Falls back to a bare /interviews on a cold load
  // (e.g. the detail page opened directly in a new tab).
  const backTo =
    (location.state as { from?: string } | null)?.from ?? "/interviews";
  const [iv, setIv] = useState<InterviewDetailRow | null>(null);
  const [study, setStudy] = useState<AdminStudy | null>(null);
  const [userDetails, setUserDetails] = useState<AdminUserDetails | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // ── Load detail ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    interviewsApi
      .get(id)
      .then((row) => setIv(row))
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [id]);

  // Study (for name display) + user details (identity snapshot) — fire in
  // parallel once the interview row is loaded so the header & cards can
  // hydrate independently.
  useEffect(() => {
    if (!iv || !id) return;
    studiesApi
      .get(iv.studyId)
      .then(({ study: s }) => setStudy(s))
      .catch(() => {
        /* non-fatal — we still have the studyId */
      });
    interviewsApi
      .userDetails(id)
      .then((d) => setUserDetails(d))
      .catch(() => setUserDetails(null));
  }, [iv?.studyId, id]);

  // Signed playback URL — only when there's audio actually recorded.
  useEffect(() => {
    if (!id || !iv?.audioGcsPath) return;
    interviewsApi
      .audioUrl(id)
      .then(({ url }) => setAudioUrl(url))
      .catch(() => setAudioUrl(null));
  }, [id, iv?.audioGcsPath]);

  // ── Derived stats — word count + share are computable from transcript,
  // so we don't depend on the API to return them. Lifted out of the
  // prototype's `computeStats` verbatim.
  const stats = useMemo(() => {
    if (!iv?.transcript) {
      return { participantWords: 0, participantShare: 0, totalWords: 0 };
    }
    const wc = (text: string) =>
      text.trim().split(/\s+/).filter(Boolean).length;
    let userWords = 0;
    let leaWords = 0;
    for (const t of iv.transcript) {
      const n = wc(t.text);
      if (t.role === "user") userWords += n;
      else leaWords += n;
    }
    const total = userWords + leaWords;
    return {
      participantWords: userWords,
      participantShare: total === 0 ? 0 : userWords / total,
      totalWords: total,
    };
  }, [iv?.transcript]);

  async function generateInsights() {
    if (!id) return;
    setInsightsBusy(true);
    setInsightsError(null);
    try {
      const { insights } = await interviewsApi.insights(id);
      setIv((prev) =>
        prev ? { ...prev, insights, sentiment: insights.sentiment } : prev,
      );
    } catch (err) {
      if (err instanceof ApiError && err.code === "transcript_too_short") {
        setInsightsError(
          "Transcript too short to analyze (needs ≥ 2 participant turns and ≥ 15 words).",
        );
      } else {
        setInsightsError(formatApiError(err));
      }
    } finally {
      setInsightsBusy(false);
    }
  }

  async function toggleArchive() {
    if (!id || !iv) return;
    try {
      const { interview } = iv.archived
        ? await interviewsApi.unarchive(id)
        : await interviewsApi.archive(id);
      setIv((prev) =>
        prev ? { ...prev, archived: interview.archived } : prev,
      );
    } catch (err) {
      setError(formatApiError(err));
    }
  }

  if (loading) {
    return (
      <div className="px-8 py-10 lg:px-14 lg:py-12 text-[13px] text-neutral-500">
        Loading interview…
      </div>
    );
  }
  if (error && !iv) {
    return (
      <div className="px-8 py-10 lg:px-14 lg:py-12">
        <ErrorBanner message={error} />
      </div>
    );
  }
  if (!iv) {
    return (
      <div className="px-8 py-10 lg:px-14 lg:py-12 text-[13px] text-neutral-500">
        Interview not found.
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-full px-8 py-10 lg:px-14 lg:py-12 max-w-5xl">
      {/* Back link */}
      <div className="mb-6">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wider text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
            <path
              d="M10 3L5 8l5 5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          All interviews
        </Link>
      </div>

      {error && <ErrorBanner message={error} />}

      {/* ── Header: email + study + pills ──────────────────────────────── */}
      <header className="mb-6">
        <h1 className="text-[28px] leading-tight font-semibold text-neutral-900 truncate mb-2">
          {iv.email}
        </h1>
        <div className="flex items-center gap-3 flex-wrap">
          {study && (
            <Link
              to={`/studies/${study.id}`}
              className="text-[13px] text-neutral-700 hover:text-neutral-900 hover:underline"
            >
              {study.name || study.slug}
            </Link>
          )}
          {study && <span className="text-neutral-300">·</span>}
          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-neutral-500">
            <span className="select-all">ID: {iv.id.slice(0, 8)}</span>
            <CopyButton value={iv.id} ariaLabel="Copy interview ID" />
          </span>
          <span className="text-neutral-300">·</span>
          <StatusTag
            status={
              iv.archived
                ? "archived"
                : iv.status === "completed_valid"
                  ? "valid"
                  : iv.status === "completed_invalid"
                    ? "invalid"
                    : "in_progress"
            }
            reason={iv.invalidReason}
          />
          <SentimentTag sentiment={iv.sentiment} />
          <span className="text-neutral-300">·</span>
          <button
            type="button"
            onClick={() => void toggleArchive()}
            className="text-[12px] text-neutral-500 hover:text-neutral-900 hover:underline"
          >
            {iv.archived ? "Unarchive" : "Archive"}
          </button>
        </div>
      </header>

      {/* Invalid reason callout */}
      {iv.invalidReason && (
        <div className="mb-6 rounded-lg border border-rose-200 bg-rose-50/40 px-4 py-3">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-700">
              Marked invalid
            </span>
          </div>
          <p className="text-[13px] text-neutral-700">
            <span className="font-medium text-rose-800">Reason: </span>
            <span className="font-mono">{iv.invalidReason}</span>
            {iv.invalidReason === "duration_below_minimum" && (
              <span className="ml-1 text-neutral-500">
                — interview was too short relative to the study's gates.
              </span>
            )}
            {iv.invalidReason === "low_participation" && (
              <span className="ml-1 text-neutral-500">
                — too few participant turns / words.
              </span>
            )}
          </p>
        </div>
      )}

      {/* ── KPI tiles ──────────────────────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        <Stat label="Duration" value={formatDuration(iv.durationS)} />
        <Stat label="Turns" value={iv.userTurnCount?.toString() ?? "—"} />
        <Stat
          label="Participant words"
          value={String(stats.participantWords)}
        />
        <Stat
          label="Participant share"
          value={`${Math.round(stats.participantShare * 100)}%`}
          hint={
            stats.participantShare >= 0.6
              ? "Great — participant did most of the talking"
              : stats.participantShare >= 0.4
                ? "Balanced"
                : stats.participantShare > 0
                  ? "Lea-heavy — consider trimming the prompt"
                  : undefined
          }
        />
        <Stat
          label="Cost (computed)"
          value={iv.costUsd != null ? `$${iv.costUsd.toFixed(4)}` : "—"}
        />
        <Stat label="Started" value={formatDateShort(iv.startedAt)} />
      </section>

      {/* ── Identity snapshot ──────────────────────────────────────────── */}
      <section className="mb-8 rounded-lg border border-neutral-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden className="text-neutral-400">
            <circle cx="8" cy="6" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path
              d="M3 14c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
            Participant identity
            {iv.snapshotAt && (
              <span className="ml-2 normal-case font-normal text-neutral-400 tracking-normal">
                · snapshot taken {formatDateShort(iv.snapshotAt)}
              </span>
            )}
          </h2>
        </div>
        {!userDetails ||
        !(
          userDetails.name ||
          userDetails.language ||
          userDetails.signupDate ||
          (userDetails.availablePlanIds &&
            userDetails.availablePlanIds.length > 0)
        ) ? (
          // No identity data — participant isn't in identity.users (or the
          // snapshot wasn't taken). Calm empty state, never an error.
          <p className="text-[12px] text-neutral-400 italic">
            No identity details for this participant.
          </p>
        ) : (
          <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
            <IdRow label="Name" value={userDetails?.name ?? null} />
            <IdRow label="Language" value={userDetails?.language ?? null} />
            <IdRow
              label="Signup date"
              value={
                userDetails?.signupDate
                  ? formatDateShort(userDetails.signupDate)
                  : null
              }
            />
            <IdRow
              label="Active plans"
              value={
                userDetails?.availablePlanIds &&
                userDetails.availablePlanIds.length > 0 ? (
                  <span className="inline-flex flex-wrap gap-1.5">
                    {userDetails.availablePlanIds.map((p) => (
                      <span
                        key={p}
                        className="inline-flex items-center rounded-md bg-neutral-100 text-neutral-700 px-2 py-0.5 text-[11px] font-medium"
                      >
                        {p}
                      </span>
                    ))}
                  </span>
                ) : null
              }
            />
          </dl>
        )}
      </section>

      {/* ── Reward ─────────────────────────────────────────────────────── */}
      <RewardPanel
        rewardSentAt={iv.rewardSentAt}
        rewardCode={iv.rewardCode}
        valid={iv.status === "completed_valid"}
      />

      {/* ── Audio + transcript row ─────────────────────────────────────── */}
      <section className="mb-8">
        <h2 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-2">
          Recording & transcript
        </h2>
        <div className="flex items-center gap-3">
          {audioUrl ? (
            <div className="flex-1 min-w-0 rounded-md border border-neutral-200 bg-white px-2 py-1.5">
              <audio
                controls
                src={audioUrl}
                className="block h-9 w-full"
                preload="metadata"
              />
            </div>
          ) : (
            <div className="flex-1 min-w-0 rounded-md border border-dashed border-neutral-200 bg-white px-3 py-2.5">
              <span className="text-[12px] text-neutral-400 italic">
                {iv.audioGcsPath
                  ? "Loading audio…"
                  : "No audio recorded for this interview."}
              </span>
            </div>
          )}

          {iv.transcript && iv.transcript.length > 0 ? (
            <button
              type="button"
              onClick={() => setTranscriptOpen(true)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-neutral-900 hover:bg-neutral-800 px-3 py-2 text-[12px] font-medium text-white transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden>
                <path
                  d="M4 2h6l3 3v9H4z M10 2v3h3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 8h5M6 10.5h5M6 13h3"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              Open transcript ({iv.transcript.length})
            </button>
          ) : (
            <span className="text-[12px] text-neutral-400 italic shrink-0 px-2">
              No transcript captured.
            </span>
          )}
        </div>
      </section>

      {/* ── Insights card ──────────────────────────────────────────────── */}
      <section className="rounded-lg border border-neutral-200 bg-white p-5 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SparkleIcon />
            <h2 className="text-[15px] font-semibold text-neutral-900">
              AI insights
            </h2>
          </div>
          {iv.insights && (
            <button
              type="button"
              onClick={() => void generateInsights()}
              disabled={insightsBusy}
              className="text-[11px] text-neutral-500 hover:text-neutral-900 hover:underline disabled:opacity-50"
            >
              {insightsBusy ? "Regenerating…" : "Regenerate"}
            </button>
          )}
        </div>

        {insightsError && (
          <p
            role="alert"
            className="mb-3 rounded-md border border-rose-200 bg-rose-50/40 px-3 py-2 text-[13px] text-rose-700"
          >
            {insightsError}
          </p>
        )}

        {iv.insights ? (
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
                Summary
              </div>
              <p className="mt-1 text-[13px] text-neutral-900 leading-relaxed">
                {iv.insights.summary}
              </p>
              {iv.insights.surprising_quote && (
                <blockquote className="mt-2 border-l-4 border-neutral-900 pl-3 py-0.5 text-[13px] text-neutral-900 italic">
                  &ldquo;{iv.insights.surprising_quote.text}&rdquo;
                  {iv.insights.surprising_quote.why && (
                    <p className="mt-1 not-italic text-[11px] text-neutral-500 leading-tight">
                      {iv.insights.surprising_quote.why}
                    </p>
                  )}
                </blockquote>
              )}
            </div>

            {iv.insights.friction_themes.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium shrink-0">
                  Themes:
                </span>
                {iv.insights.friction_themes.map((t, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-medium text-neutral-800 bg-neutral-100 rounded-md px-2 py-0.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1.5">
                Key takeaways
              </div>
              <ul className="space-y-1.5">
                {iv.insights.takeaways.map((t, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-[13px] text-neutral-900 leading-relaxed"
                  >
                    <span className="text-neutral-700 font-semibold shrink-0">
                      {i + 1}.
                    </span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {iv.insights.followup_question && (
              <div className="rounded-md bg-neutral-50 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-1">
                  Follow-up for next time
                </div>
                <p className="text-[13px] text-neutral-700 italic">
                  {iv.insights.followup_question}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-[13px] text-neutral-500 mb-3 max-w-md mx-auto leading-relaxed">
              No insights yet. Generate AI analysis to extract a
              summary, themes, takeaways, and a surprising quote from the
              transcript.
            </p>
            <button
              type="button"
              onClick={() => void generateInsights()}
              disabled={insightsBusy || !iv.transcript || iv.transcript.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-300 px-4 py-2 text-[13px] font-medium text-white transition-colors"
            >
              <SparkleIcon />
              {insightsBusy ? "Generating…" : "Generate insights"}
            </button>
          </div>
        )}
      </section>

      <button
        type="button"
        onClick={() => navigate(backTo)}
        className="text-[12px] text-neutral-500 hover:text-neutral-900 hover:underline"
      >
        ← Back to interviews
      </button>

      {/* Transcript modal */}
      {transcriptOpen && iv.transcript && (
        <TranscriptModal
          transcript={iv.transcript}
          email={iv.email}
          endedAt={iv.endedAt}
          onClose={() => setTranscriptOpen(false)}
        />
      )}
    </div>
  );
}

// ── Small components ──────────────────────────────────────────────────────

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-md border border-neutral-200 bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
        {label}
      </div>
      <div className="mt-1 text-[22px] font-semibold text-neutral-900 tabular-nums leading-none">
        {value}
      </div>
      {hint && (
        <div className="mt-1.5 text-[10px] text-neutral-500 leading-tight">
          {hint}
        </div>
      )}
    </div>
  );
}

function IdRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <>
      <dt className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">
        {label}
      </dt>
      <dd className="text-[13px] text-neutral-900">
        {value ?? <span className="text-neutral-400">—</span>}
      </dd>
    </>
  );
}

/** Reward status + the exact gift-card code emailed to this participant. The
 *  code is shown in full (intentional — admins need it to reconcile payouts)
 *  with a copy button. */
function RewardPanel({
  rewardSentAt,
  rewardCode,
  valid,
}: {
  rewardSentAt: string | null;
  rewardCode: string | null;
  valid: boolean;
}) {
  return (
    <section className="mb-8 rounded-lg border border-neutral-200 bg-white p-5">
      <h2 className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-3">
        Reward
      </h2>
      {rewardSentAt ? (
        <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
          <IdRow
            label="Status"
            value={
              <span className="inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border bg-emerald-50 text-emerald-700 border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                Sent
              </span>
            }
          />
          <IdRow label="Sent at" value={formatDateShort(rewardSentAt)} />
          <IdRow
            label="Gift-card code"
            value={
              rewardCode ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-mono text-[13px] text-neutral-900 break-all">
                    {rewardCode}
                  </span>
                  <CopyButton
                    value={rewardCode}
                    ariaLabel="Copy gift-card code"
                  />
                </span>
              ) : null
            }
          />
        </dl>
      ) : valid ? (
        <span className="inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border bg-amber-50 text-amber-700 border-amber-200">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          Pending — no code sent yet
        </span>
      ) : (
        <span className="text-[12px] text-neutral-400 italic">
          Not eligible — only valid interviews receive a reward.
        </span>
      )}
    </section>
  );
}

function SparkleIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      aria-hidden
      className="text-neutral-700"
    >
      <path
        d="M8 1.5l1.2 3.6 3.8 1.2-3.8 1.2L8 11.1 6.8 7.5 3 6.3l3.8-1.2L8 1.5zM12.5 11l.6 1.8 1.9.6-1.9.6-.6 1.8-.6-1.8-1.8-.6 1.8-.6.6-1.8z"
        fill="currentColor"
      />
    </svg>
  );
}

// ── Status + sentiment tags (mirror Interviews.tsx; tiny redefinition is
//    cheaper than threading an export across pages). ────────────────────────

type RowStatus = "valid" | "invalid" | "in_progress" | "archived";

function StatusTag({
  status,
  reason: _reason,
}: {
  status: RowStatus;
  reason: string | null;
}) {
  const cfg: Record<
    RowStatus,
    { label: string; cls: string; dot: string }
  > = {
    valid: {
      label: "Valid",
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-400",
    },
    invalid: {
      label: "Invalid",
      cls: "bg-rose-50 text-rose-700 border-rose-200",
      dot: "bg-rose-400",
    },
    in_progress: {
      label: "In progress",
      cls: "bg-amber-50 text-amber-700 border-amber-200",
      dot: "bg-amber-400",
    },
    archived: {
      label: "Archived",
      cls: "bg-neutral-100 text-neutral-600 border-neutral-200",
      dot: "bg-neutral-400",
    },
  };
  const c = cfg[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${c.cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function SentimentTag({ sentiment }: { sentiment: string | null }) {
  const s = sentiment ?? "not_detected";
  const cfg: Record<string, { label: string; cls: string; dot: string }> = {
    positive: {
      label: "Positive",
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-400",
    },
    neutral: {
      label: "Neutral",
      cls: "bg-amber-50 text-amber-700 border-amber-200",
      dot: "bg-amber-400",
    },
    negative: {
      label: "Negative",
      cls: "bg-rose-50 text-rose-700 border-rose-200",
      dot: "bg-rose-400",
    },
    not_detected: {
      label: "No sentiment",
      cls: "bg-neutral-100 text-neutral-600 border-neutral-200",
      dot: "bg-neutral-400",
    },
  };
  const c = cfg[s] ?? cfg.not_detected!;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${c.cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

// ── Transcript modal (port of `_prototype/components/studies/TranscriptModal`)

function TranscriptModal({
  transcript,
  email,
  endedAt,
  onClose,
}: {
  transcript: Array<{ role: "user" | "assistant"; text: string; ts?: number }>;
  email: string;
  endedAt: string | null;
  onClose: () => void;
}) {
  // Esc to close + lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="transcript-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-neutral-200">
          <div className="min-w-0">
            <h2
              id="transcript-modal-title"
              className="text-[16px] font-semibold text-neutral-900"
            >
              Transcript
            </h2>
            <p className="mt-0.5 text-[12px] text-neutral-500 truncate">
              <span>{email}</span>
              {endedAt && (
                <>
                  <span className="text-neutral-300 mx-1.5">·</span>
                  <span>{formatDateShort(endedAt)}</span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close transcript"
            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {transcript.length === 0 ? (
            <p className="text-[13px] text-neutral-500 italic">
              No transcript captured.
            </p>
          ) : (
            <ol className="space-y-3">
              {transcript.map((t, i) => (
                <li
                  key={i}
                  className={`text-[13px] leading-relaxed ${
                    t.role === "assistant"
                      ? "text-neutral-900"
                      : "text-neutral-600"
                  }`}
                >
                  <span
                    className={`mr-2 inline-block text-[10px] font-semibold uppercase tracking-wider ${
                      t.role === "assistant"
                        ? "text-neutral-700"
                        : "text-neutral-400"
                    }`}
                  >
                    {t.role === "assistant" ? "Lea" : "Participant"}
                  </span>
                  <span>{t.text}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-neutral-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-200 bg-white hover:bg-neutral-50 hover:border-neutral-300 px-3 py-1.5 text-[12px] font-medium text-neutral-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Format helpers ─────────────────────────────────────────────────────────

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const month = MONTHS_SHORT[d.getUTCMonth()];
  const day = d.getUTCDate();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${month} ${day} · ${hh}:${mm}`;
}
